import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Input,
  Button,
  Space,
  Table,
  Upload,
  Modal,
  Form,
  Tag,
  Row,
  Col,
  Typography,
  Alert,
  Steps,
  App,
  Empty,
  Divider,
  Progress
} from 'antd';
import {
  InboxOutlined,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  FolderOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/rolePermissions';
import archiveService from '../../services/archiveService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ARCHIVE_UPLOAD_ACCEPT, ARCHIVE_UPLOAD_FORMATS_LABEL, isAllowedArchiveFile } from '../../utils/archiveUploadTypes';

dayjs.extend(relativeTime);

const { Search } = Input;
const { Text } = Typography;
const { Dragger } = Upload;
const { Step } = Steps;

const Archive = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { currentUser } = useContext(AuthContext);

  // Role-based permissions - all authenticated users can create, upload, delete
  const userRole = currentUser?.role;
  const canCreate = userRole === USER_ROLES.USER || userRole === USER_ROLES.TENANT_ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
  const canUpload = userRole === USER_ROLES.USER || userRole === USER_ROLES.TENANT_ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
  const canDelete = userRole === USER_ROLES.USER || userRole === USER_ROLES.TENANT_ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
  const isReadOnly = false; // All users have write access

  // State
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Quick Start Modal
  const [quickStartModalOpen, setQuickStartModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [projectDetails, setProjectDetails] = useState(null);

  // File upload
  const [fileList, setFileList] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await archiveService.getAllProjects();
      const sortedProjects = data.sort((a, b) =>
        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      );
      setProjects(sortedProjects);
      setFilteredProjects(sortedProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
      message.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [message]);

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Filter projects based on search
  useEffect(() => {
    if (searchText.trim()) {
      const filtered = projects.filter(project =>
        project.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchText, projects]);

  // Quick Start Modal - Create project + Upload files in one flow
  const handleQuickStart = () => {
    form.resetFields();
    setFileList([]);
    setCurrentStep(0);
    setQuickStartModalOpen(true);
  };

  const handleQuickStartSubmit = async () => {
    try {
      if (currentStep === 0) {
        // Step 1: Validate and store project details, then move to file upload
        const values = await form.validateFields();
        setProjectDetails({
          name: values.projectName.trim(),
          description: values.description || ''
        });
        setCurrentStep(1);
        return;
      }

      // Step 2: Create project and upload files
      if (fileList.length === 0) {
        message.warning('Please add at least one file');
        return;
      }

      setUploadLoading(true);
      setUploadProgress({ current: 0, total: fileList.length });

      // Create project using stored details
      const newProject = await archiveService.createProject(projectDetails);

      // Upload files one by one to track progress
      let uploadedCount = 0;
      const failedFiles = [];

      for (const file of fileList) {
        try {
          await archiveService.uploadDocument('project', newProject._id, file.originFileObj);
          uploadedCount++;
          setUploadProgress({ current: uploadedCount, total: fileList.length });
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          failedFiles.push({ name: file.name, error: err.message || 'Unknown error' });
          // Continue with other files even if one fails
        }
      }

      // Show detailed feedback based on results
      if (failedFiles.length === 0) {
        message.success(`Project "${projectDetails.name}" created with ${uploadedCount} files`);
      } else if (uploadedCount === 0) {
        message.error({
          content: `Project "${projectDetails.name}" created, but all ${fileList.length} files failed to upload. Failed files: ${failedFiles.map(f => f.name).join(', ')}`,
          duration: 10
        });
      } else {
        message.warning({
          content: `Project "${projectDetails.name}" created. Uploaded ${uploadedCount} of ${fileList.length} files. ${failedFiles.length} file(s) failed: ${failedFiles.map(f => f.name).join(', ')}`,
          duration: 10
        });
      }

      // Reset and close
      setQuickStartModalOpen(false);
      setFileList([]);
      setProjectDetails(null);
      setCurrentStep(0);
      setUploadProgress({ current: 0, total: 0 });
      form.resetFields();
      fetchProjects();
    } catch (err) {
      console.error('Error in quick start:', err);
      message.error('Failed to create project or upload files');
    } finally {
      setUploadLoading(false);
    }
  };

  // Quick upload to existing project
  const handleQuickUpload = async (project, fileInfo) => {
    try {
      setUploadLoading(true);
      setUploadProgress({ current: 0, total: fileInfo.fileList.length });

      // Upload files one by one to track progress (same as new project upload)
      let uploadedCount = 0;
      const failedFiles = [];

      for (const file of fileInfo.fileList) {
        try {
          await archiveService.uploadDocument('project', project._id, file.originFileObj);
          uploadedCount++;
          setUploadProgress({ current: uploadedCount, total: fileInfo.fileList.length });
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          failedFiles.push({ name: file.name, error: err.message || 'Unknown error' });
          // Continue with other files even if one fails
        }
      }

      // Show detailed feedback based on results
      if (failedFiles.length === 0) {
        message.success(`Uploaded ${uploadedCount} file(s) to "${project.name}"`);
      } else if (uploadedCount === 0) {
        message.error({
          content: `All ${fileInfo.fileList.length} files failed to upload to "${project.name}". Failed files: ${failedFiles.map(f => f.name).join(', ')}`,
          duration: 10
        });
      } else {
        message.warning({
          content: `Uploaded ${uploadedCount} of ${fileInfo.fileList.length} files to "${project.name}". ${failedFiles.length} file(s) failed: ${failedFiles.map(f => f.name).join(', ')}`,
          duration: 10
        });
      }

      fetchProjects();
    } catch (err) {
      console.error('Error uploading files:', err);
      message.error('Failed to upload files');
    } finally {
      setUploadLoading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Navigate to project detail page
  const handleViewProject = (project) => {
    navigate(`/archive/${project._id}`);
  };

  // Delete project with modal confirmation
  const handleDeleteProject = (projectId, projectName) => {
    Modal.confirm({
      title: 'Delete Project',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to delete "{projectName}"?</p>
          <p>This will permanently delete the project and all its uploaded files.</p>
          <p><strong>This action cannot be undone.</strong></p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await archiveService.deleteProject(projectId);
          message.success(`Deleted project "${projectName}"`);
          fetchProjects();
        } catch (err) {
          console.error('Error deleting project:', err);
          message.error('Failed to delete project');
        }
      }
    });
  };


  // Upload props for Dragger
  const draggerProps = {
    multiple: true,
    accept: ARCHIVE_UPLOAD_ACCEPT,
    fileList: fileList,
    beforeUpload: (file) => {
      if (!isAllowedArchiveFile(file)) {
        message.error(`${file.name} is not a valid file type`);
        return Upload.LIST_IGNORE;
      }

      return false; // Prevent auto upload
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    onRemove: (file) => {
      setFileList(fileList.filter(f => f.uid !== file.uid));
    }
  };

  // Get recent projects (last 5)
  const recentProjects = filteredProjects.slice(0, 5);

  // Table columns
  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => (
        <Space>
          <FolderOutlined style={{ color: 'var(--rlc-text-accent)' }} />
          <Text strong>{name}</Text>
        </Space>
      )
    },
    {
      title: 'Files',
      dataIndex: 'documents',
      key: 'document_count',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.documents?.length || 0) - (b.documents?.length || 0),
      render: (documents) => (
        <Tag color="blue">{documents?.length || 0}</Tag>
      )
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      sorter: (a, b) => new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at),
      render: (date, record) => (
        <Text type="secondary">
          {dayjs(date || record.created_at).fromNow()}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          {canUpload && (
            <Upload
              {...draggerProps}
              showUploadList={false}
              onChange={(info) => handleQuickUpload(record, info)}
            >
              <Button type="link" icon={<UploadOutlined />} loading={uploadLoading}>
                Add Files
              </Button>
            </Upload>
          )}
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewProject(record)}>
            View
          </Button>
          {canDelete && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteProject(record._id, record.name)}
            >
              Delete
            </Button>
          )}
        </Space>
      )
    }
  ];


  return (
    <div style={{ background: 'var(--rlc-surface-sunken)', height: 'calc(100vh - 56px)', overflow: 'auto' }}>
      {/* Quick Actions Bar */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {isReadOnly && (
              <Alert
                message="Read-only access"
                description="You can view and search projects, but cannot create, upload, or delete."
                type="info"
                showIcon
                style={{ flex: 1, marginRight: 16 }}
              />
            )}
            {!isReadOnly && <div></div>}
            {canCreate && (
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={handleQuickStart}
              >
                New Project
              </Button>
            )}
          </div>

          <Search
            placeholder="Search projects..."
            size="large"
            allowClear
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 600 }}
          />
        </Space>
      </Card>

      {/* Recent Projects */}
      {recentProjects.length > 0 && (
        <Card
          title={
            <Space>
              <ClockCircleOutlined />
              <Text>Recent Projects</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            {recentProjects.map(project => (
              <Col xs={24} sm={12} lg={8} xl={6} key={project._id}>
                <Card
                  hoverable
                  onClick={() => handleViewProject(project)}
                  style={{ height: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <FolderOutlined style={{ fontSize: 24, color: 'var(--rlc-text-accent)' }} />
                      <Text strong ellipsis>{project.name}</Text>
                    </Space>
                    <Text type="secondary">
                      {project.documents?.length || 0} files
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(project.updated_at || project.created_at).fromNow()}
                    </Text>
                    {canUpload && (
                      <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Upload
                          {...draggerProps}
                          showUploadList={false}
                          onChange={(info) => handleQuickUpload(project, info)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="link"
                            icon={<UploadOutlined />}
                            block
                            loading={uploadLoading}
                          >
                            Quick Add Files
                          </Button>
                        </Upload>
                      </>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* All Projects Table */}
      <Card title={<Text strong>All Projects</Text>}>
        <Table
          columns={columns}
          dataSource={filteredProjects}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} projects`
          }}
          locale={{
            emptyText: (
              <Empty
                description={isReadOnly ? "No projects to view" : "No projects yet"}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                {canCreate && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleQuickStart}>
                    Create Your First Project
                  </Button>
                )}
              </Empty>
            )
          }}
        />
      </Card>

      {/* Quick Start Modal */}
      <Modal
        title="Create New Project"
        open={quickStartModalOpen}
        onCancel={() => {
          setQuickStartModalOpen(false);
          setFileList([]);
          setProjectDetails(null);
          setCurrentStep(0);
          setUploadProgress({ current: 0, total: 0 });
          form.resetFields();
        }}
        footer={[
          currentStep === 1 && (
            <Button key="back" onClick={() => setCurrentStep(0)}>
              Back
            </Button>
          ),
          <Button key="cancel" onClick={() => {
            setQuickStartModalOpen(false);
            setFileList([]);
            setProjectDetails(null);
            setCurrentStep(0);
            setUploadProgress({ current: 0, total: 0 });
            form.resetFields();
          }}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploadLoading}
            onClick={handleQuickStartSubmit}
          >
            {currentStep === 0 ? 'Next' : `Create & Upload ${fileList.length} Files`}
          </Button>
        ]}
        width={700}
      >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title="Project Details" />
          <Step title="Upload Files" />
        </Steps>

        {currentStep === 0 ? (
          <Form form={form} layout="vertical">
            <Form.Item
              label="Project Name"
              name="projectName"
              rules={[
                { required: true, message: 'Please enter a project name' },
                { min: 3, message: 'Project name must be at least 3 characters' }
              ]}
            >
              <Input
                placeholder="e.g., Q4 Sales Review"
                size="large"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              label="Description (Optional)"
              name="description"
            >
              <Input.TextArea
                placeholder="Add a brief description of this project"
                rows={3}
              />
            </Form.Item>
          </Form>
        ) : (
          <div>
            <Alert
              message="Add Files to Your Project"
              description={`You can add ${ARCHIVE_UPLOAD_FORMATS_LABEL} files. Drag & drop multiple files or click to browse.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Dragger {...draggerProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: 48, color: 'var(--rlc-text-accent)' }} />
              </p>
              <p className="ant-upload-text">
                Drag & drop files here or click to browse
              </p>
              <p className="ant-upload-hint">
                Supported formats: {ARCHIVE_UPLOAD_FORMATS_LABEL} • Batch upload up to 50 files
              </p>
            </Dragger>

            {fileList.length > 0 && !uploadLoading && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Files ready for upload: {fileList.length}</Text>
              </div>
            )}

            {uploadLoading && uploadProgress.total > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Uploading files: {uploadProgress.current} of {uploadProgress.total}
                </Text>
                <Progress
                  percent={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
                  status={uploadProgress.current === uploadProgress.total ? 'success' : 'active'}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default Archive;
